"use client";

import React, { useEffect, useState } from "react";
import {
  UserGroupIcon,
  XMarkIcon,
  CalendarDaysIcon,
  TagIcon,
  ChartBarIcon,
  ArrowPathIcon,
  ClockIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

function formatNumber(value) {
  if (value === null || value === undefined) return "0";

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  if (num >= 1_000_000)
    return (num / 1_000_000).toFixed(2).replace(/\.00$/, "") + "M";

  if (num >= 1_000)
    return (num / 1_000).toFixed(2).replace(/\.00$/, "") + "K";

  return num.toString();
}

function formatPercent(value) {
  if (!value) return "0%";

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  if (num >= 1_000_000)
    return (num / 1_000_000).toFixed(2).replace(/\.00$/, "") + "M%";

  if (num >= 1_000)
    return (num / 1_000).toFixed(2).replace(/\.00$/, "") + "K%";

  return num + "%";
}

export default function AnalyticsCards() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const fetchStats = async () => {
      try {
        setLoading(true);

        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/analytics/analytics-veriables/`, {
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setStats(await res.json());
      } catch (err) {
        console.error("❌ Failed to fetch analytics:", err);
        setError("Failed to load analytics cards data.");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token, navigate]);

  const currency = stats.currency ?? "";

  const cards = [
    {
      title: "N° of your customers",
      value: stats.clients_count ?? 0,
      change: formatPercent(stats.client_growth_percentage),
      icon: <UserGroupIcon className="w-6 h-6 text-white" />,
    },
    {
      title: "Cancellation Rate",
      value: stats.cancellation_rate ?? "0%",
      change: formatPercent(stats.cancellation_rate_growth_percentage),
      icon: <XMarkIcon className="w-6 h-6 text-white" />,
    },
    {
      title: "Services per day",
      value: stats.services_per_day ?? 0,
      change: formatPercent(stats.services_per_day_growth_percentage),
      icon: <ChartBarIcon className="w-6 h-6 text-white" />,
    },
    {
      title: "Average ticket",
      value: `${formatNumber(stats.averige_tiket)} ${currency}`,
      change: formatPercent(stats.averige_tiket_growth_percentage),
      icon: <TagIcon className="w-6 h-6 text-white" />,
    },
    {
      title: "Appointments per client",
      value: stats.appointments_per_client ?? 0,
      change: formatPercent(stats.appointments_per_client_growth_percentage),
      icon: <CalendarDaysIcon className="w-6 h-6 text-white" />,
    },
    {
      title: "Conversion rate",
      value: "0%",
      change: "0%",
      icon: <ArrowPathIcon className="w-6 h-6 text-white" />,
    },
    {
      title: "Client Retention Rate",
      value: "0%",
      change: "0%",
      icon: <ClockIcon className="w-6 h-6 text-white" />,
    },
    {
      title: "Client's Lifetime Value",
      value: `${formatNumber(stats.clients_lifetime_value)} ${currency}`,
      change: formatPercent(stats.clients_lifetime_value_growth_percentage),
      icon: <CurrencyDollarIcon className="w-6 h-6 text-white" />,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-6">
      {loading ? (
        <div className="col-span-4 flex justify-center items-center text-neutral-400">
          Loading analytics...
        </div>
      ) : error ? (
        <div className="col-span-4 text-red-500 text-sm italic">
          {error}
        </div>
      ) : (
        cards.map((item, i) => (
          <div
            key={i}
            className="flex flex-col bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#5563F6] p-3 rounded-full">{item.icon}</div>
              <span className="text-[#302B2B] font-medium text-base">
                {item.title}
              </span>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#302B2B] leading-tight">
                {item.value}
              </p>
              <p className="text-sm text-[#9A99AB] mt-1">
                Growth: {item.change}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  );
}